import React, { useState, useEffect } from "react";
import api from '../api/api';
import { useNavigate } from 'react-router-dom';

const PayFast = ({ orderNumber, price, sellerId, sellerName, shippingInfo, currencySymbol = "$" }) => {
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [payfastConfig, setPayfastConfig] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPayFastConfig = async () => {
      try {
        // Use sellerId from props for API, but sellerName for URLs
        if (!sellerId) {
          setError('Seller ID not found');
          return;
        }

        console.log('Fetching PayFast config for seller:', sellerId);
        // Fetch seller's enabled payment methods (includes PayFast config)
        const { data } = await api.get(`/seller/${sellerId}/payment-methods`);
        console.log('PayFast config response:', data);
        
        // Check if PayFast is enabled for this seller
        if (data.success && data.config.payfast && data.config.payfast.isEnabled) {
          setPayfastConfig(data.config.payfast);
          console.log('PayFast config loaded successfully');
        } else {
          console.log('PayFast not enabled for this seller:', data);
          setError('PayFast is not enabled or configured for this seller');
        }
      } catch (error) {
        console.error('Error fetching PayFast config:', error);
        setError('Failed to load PayFast configuration: ' + error.message);
      }
    };

    fetchPayFastConfig();
  }, []);

  const handlePayFastPayment = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Store order number in localStorage for confirmation page (same as PayPal)
      localStorage.setItem('orderNumber', orderNumber);

      // Prepare payment data using shipping info for guest checkout
      const paymentData = {
        name_first: shippingInfo?.name?.split(' ')[0] || 'Customer',
        name_last: shippingInfo?.name?.split(' ').slice(1).join(' ') || 'Name',
        email_address: shippingInfo?.email || 'customer@example.com',
        amount: price,
        item_name: `Order #${orderNumber}`,
        m_payment_id: orderNumber,
        orderNumber: orderNumber,
        sellerId: sellerId, // Add sellerId for return URL generation
        sellerName: sellerName // Add sellerName for URL routing
      };

      console.log('Creating PayFast payment with data:', paymentData);

      // Create PayFast payment
      const { data } = await api.post('/payment/create-payfast-payment', paymentData);
      console.log('PayFast payment response:', data);

      if (data && data.form) {
        // Create a temporary div to hold the form
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = data.form;
        const form = tempDiv.querySelector('#payfast-form');
        
        if (form) {
          // Submit the form to redirect to PayFast
          document.body.appendChild(form);
          form.submit();
        } else {
          throw new Error('PayFast form generation failed');
        }
      } else {
        throw new Error(data?.message || 'Failed to create PayFast payment');
      }
    } catch (error) {
      console.error('PayFast payment error:', error);
      setError(error.response?.data?.message || error.message || 'Payment initialization failed');
    } finally {
      setIsLoading(false);
    }
  };

  if (!payfastConfig) {
    return <div className="mt-4 text-center">Loading PayFast...</div>;
  }

  if (error) {
    return (
      <div className="mt-4">
        <div className="text-red-500 text-center mb-4">{error}</div>
        <button 
          onClick={() => window.location.reload()} 
          className="w-full bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="mt-4">
      <div className="bg-gray-50 p-4 rounded-lg mb-4">
        <div className="flex items-center mb-3">
          <img 
            src="/images/payment/payfast.png" 
            alt="PayFast" 
            className="h-8 w-auto mr-3"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          <h3 className="text-lg font-semibold">PayFast Payment</h3>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          You will be redirected to PayFast to complete your payment securely.
        </p>
        <div className="text-sm text-gray-500">
          <p><strong>Amount:</strong> {currencySymbol}{price}</p>
          <p><strong>Order:</strong> #{orderNumber}</p>
          <p><strong>Environment:</strong> {payfastConfig.environment}</p>
        </div>
      </div>

      <button
        onClick={handlePayFastPayment}
        disabled={isLoading}
        className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
          isLoading 
            ? 'bg-gray-400 cursor-not-allowed' 
            : 'bg-green-600 hover:bg-green-700 text-white'
        }`}
      >
        {isLoading ? (
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
            Processing...
          </div>
        ) : (
          `Pay with PayFast - ${currencySymbol}${price}`
        )}
      </button>

      <div className="mt-3 text-xs text-gray-500 text-center">
        Secure payment powered by PayFast
      </div>
    </div>
  );
};

export default PayFast; 